import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Star, Phone } from 'lucide-react';

/**
 * SEO-rich content block with:
 * - Local NAP (name, address, phone) — critical for local SEO
 * - Service-area cities (Cary, Raleigh, Durham, Apex, Chapel Hill)
 * - Keyword-dense H2s: "Indian Clothing Boutique in Morrisville NC", "Bridal Wear",
 *   "Sarees", "Kurti Sets", "Lehenga Rental", "Indian Party Wear"
 * - Deep-links to /lehengas, /sarees, /kurti-sets, /bridal-wear, /rentals
 * - Google Reviews + Maps links
 */
export const SEOContent = () => {
  const services = [
    { path: '/bridal-wear', title: 'Indian Bridal Wear', body: 'Heirloom-worthy bridal lehengas, sarees & Anarkalis for phera, reception and sangeet.' },
    { path: '/lehengas', title: 'Designer Lehengas', body: 'Sequin cocktail, mirror-work and hand-embroidered velvet bridal lehengas.' },
    { path: '/sarees', title: 'Indian Sarees', body: 'Banarasi silk, Kanjivaram, organza & designer pre-drape sarees for weddings & festivals.' },
    { path: '/shararas', title: 'Sharara Suits', body: 'Sharara & gharara sets and Anarkali-sharara pairings for festive and bridal wear.' },
    { path: '/kurti-sets', title: 'Kurti Sets', body: 'Everyday festive kurta sets, palazzo suits, cord sets and Anarkalis.' },
    { path: '/gowns', title: 'Party Wear Gowns', body: 'Indo-western gowns and cocktail silhouettes for receptions and sangeets.' },
    { path: '/men', title: "Men's Kurta Pajama", body: 'Sherwanis, Nehru jackets, Indo-western sets and everyday kurtas for men.' },
    { path: '/kids', title: 'Kids Indian Clothing', body: 'Girls lehengas, boys kurta sets and family-matching outfits for celebrations.' },
    { path: '/plus-size', title: 'Plus Size Indian Wear', body: 'XL – XXXL with custom sizing available on every piece.' },
    { path: '/contact', title: 'Visit the Boutique', body: 'Book a private appointment at our Morrisville, NC showroom.' },
  ];

  return (
    <section id="seo-content" className="py-24 lg:py-32 bg-ivory">
      <div className="container-luxe">
        <div className="max-w-4xl">
          <span className="overline">Serving the Triangle · North Carolina</span>
          <h2 className="font-display text-4xl lg:text-5xl text-charcoal mt-3 leading-tight">
            Indian Clothing Boutique in <em className="text-maroon not-italic font-medium">Morrisville, NC</em>.
          </h2>
          <div className="mt-5 h-px w-16 bg-gold" />
          <div className="mt-8 space-y-5 text-charcoal/75 leading-relaxed text-base lg:text-lg">
            <p>
              <strong className="text-charcoal font-medium">Trendy Closet</strong> is a family-owned Indian clothing
              boutique in Morrisville, North Carolina, curating premium ethnic wear for women, men and kids since 2020.
              Our 600+ sq. ft. in-house showroom proudly serves the entire Triangle — including
              {' '}<strong className="text-charcoal">Cary, Raleigh, Durham, Apex, Chapel Hill, RTP and Wake Forest</strong>.
            </p>
            <p>
              Whether you&apos;re a bride searching for your dream lehenga, a wedding guest looking for a saree,
              or a family shopping for coordinated Diwali or Eid outfits, our owner Shally Chathey personally
              curates a look for every guest. We also offer in-house tailoring &amp; alterations plus US-wide shipping.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brandborder/60 border border-brandborder/60">
          {services.map((s, i) => (
            <motion.div
              key={s.path}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="bg-ivory p-8 lg:p-10"
            >
              <h3 className="font-display text-2xl text-charcoal tracking-tight">{s.title}</h3>
              <p className="text-sm text-charcoal/70 mt-3 leading-relaxed">{s.body}</p>
              <Link
                to={s.path}
                data-testid={`seo-service-${s.path.slice(1)}`}
                className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-maroon hover:text-burgundy transition-colors"
              >
                Explore {s.title} →
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Local NAP block */}
        <div className="mt-16 grid md:grid-cols-3 gap-8 border-t border-brandborder pt-10">
          <div>
            <div className="overline mb-3"><MapPin className="w-3 h-3 inline-block mr-1 text-gold" /> Visit us</div>
            <address className="not-italic text-sm text-charcoal/80 leading-relaxed">
              <strong className="text-charcoal font-medium block">Trendy Closet Indian Clothing</strong>
              158 Liberty Rose Dr<br />
              Morrisville, NC 27560<br />
              <a href="https://www.google.com/maps/dir/?api=1&destination=158+Liberty+Rose+Dr+Morrisville+NC+27560" target="_blank" rel="noreferrer" className="text-maroon hover:underline underline-offset-4 mt-2 inline-block">Find us on Google Maps →</a>
            </address>
          </div>
          <div>
            <div className="overline mb-3"><Phone className="w-3 h-3 inline-block mr-1 text-gold" /> Contact</div>
            <p className="text-sm text-charcoal/80 leading-relaxed">
              <a href="tel:+19843779835" className="text-charcoal font-medium hover:text-maroon">984-377-9835</a><br />
              <a href="mailto:chathleyshally@gmail.com" className="hover:text-maroon">chathleyshally@gmail.com</a><br />
              <span className="text-charcoal/60 text-xs uppercase tracking-[0.15em] mt-3 inline-block">Open Daily · 4:30 PM – 8:30 PM</span>
            </p>
          </div>
          <div>
            <div className="overline mb-3"><Star className="w-3 h-3 inline-block mr-1 text-gold fill-gold" /> Reviews</div>
            <p className="text-sm text-charcoal/80 leading-relaxed">
              <span className="text-charcoal font-medium">5.0 · 40+ five-star reviews</span><br />
              <a
                href="https://www.google.com/search?q=Trendy+Closet+Indian+Clothing+Reviews"
                target="_blank" rel="noreferrer"
                className="text-maroon hover:underline underline-offset-4 mt-2 inline-block"
              >
                Visit our Google Reviews →
              </a>
            </p>
          </div>
        </div>

        {/* Service areas */}
        <div className="mt-10 text-sm text-charcoal/65">
          <span className="overline">Service areas · </span>
          Morrisville · Cary · Raleigh · Durham · Apex · Chapel Hill · RTP · Wake Forest · Holly Springs · Fuquay-Varina · Garner · Knightdale · plus US-wide shipping.
        </div>

        {/* Nationwide reach — Phase 2 SEO */}
        <div className="mt-16 border-t border-brandborder pt-12">
          <div className="max-w-4xl">
            <span className="overline">USA-wide shipping</span>
            <h2 className="font-display text-3xl lg:text-4xl text-charcoal mt-3 leading-tight">
              Shop <em className="text-maroon not-italic font-medium">Indian Ethnic Wear</em> Across the USA.
            </h2>
            <div className="mt-5 h-px w-16 bg-gold" />
            <div className="mt-8 space-y-4 text-charcoal/80 leading-relaxed">
              <p>
                Trendy Closet ships Indian ethnic wear coast to coast — from designer lehengas and sarees to
                sharara suits, kurti sets, gowns, bridal outfits, men&apos;s kurta pajama and kids Indian clothing.
                Whether you&apos;re in New York, Dallas, Chicago, Atlanta, Seattle or right here in Morrisville, our
                curated selection is a WhatsApp away.
              </p>
              <p>
                Locally, we&apos;re the go-to <Link to="/indian-clothing-morrisville-nc" className="text-maroon hover:underline underline-offset-4">Indian clothing boutique in Morrisville, NC</Link>, and
                minutes away for <Link to="/indian-clothing-raleigh-nc" className="text-maroon hover:underline underline-offset-4">Raleigh customers</Link>.
                Nationwide, browse the closet at <Link to="/indian-clothing-usa" className="text-maroon hover:underline underline-offset-4">Indian Clothing USA →</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOContent;
